import React, { useId } from 'react';
import type {
  AuditAction,
  AuditLogActorOption,
  AuditLogFilters,
  AuditLogFilterState,
  AuditResourceType,
} from '../types/audit-log.types';
import { actorKey, getActionLabel, toRange } from '../utils/audit-log-utils';

interface AuditLogFilterBarProps {
  filters: AuditLogFilterState;
  options?: AuditLogFilters;
  fromDay?: string;
  toDay?: string;
  onFilterChange: (filters: AuditLogFilterState, fromDay?: string, toDay?: string) => void;
  onClear: () => void;
  isLoading?: boolean;
}

export const AuditLogFilterBar: React.FC<AuditLogFilterBarProps> = ({
  filters,
  options,
  fromDay = '',
  toDay = '',
  onFilterChange,
  onClear,
}) => {
  const actorIdAttr = useId();
  const actionIdAttr = useId();
  const resourceTypeIdAttr = useId();
  const resourceIdAttr = useId();
  const fromDateAttr = useId();
  const toDateAttr = useId();

  const selectedActorKey = filters.actorType
    ? filters.actorType === 'system'
      ? 'system:'
      : `${filters.actorType}:${filters.actorId ?? ''}`
    : 'all';

  const handleActorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === 'all') {
      const rest = { ...filters };
      delete rest.actorType;
      delete rest.actorId;
      onFilterChange(rest, fromDay, toDay);
      return;
    }

    const matchedActor = options?.actors.find((a) => actorKey(a) === val);
    if (!matchedActor) {
      // Direct parse
      const [type, id] = val.split(':');
      if (type === 'system') {
        const next = { ...filters, actorType: 'system' as const };
        delete next.actorId;
        onFilterChange(next, fromDay, toDay);
      } else {
        onFilterChange(
          { ...filters, actorType: type as any, actorId: id || undefined },
          fromDay,
          toDay
        );
      }
      return;
    }

    if (matchedActor.actorType === 'system' || !matchedActor.actorId) {
      const rest = { ...filters };
      delete rest.actorId;
      onFilterChange({ ...rest, actorType: 'system' }, fromDay, toDay);
    } else {
      onFilterChange(
        { ...filters, actorType: matchedActor.actorType, actorId: matchedActor.actorId },
        fromDay,
        toDay
      );
    }
  };

  const handleActionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === 'all') {
      const rest = { ...filters };
      delete rest.action;
      onFilterChange(rest, fromDay, toDay);
    } else {
      onFilterChange({ ...filters, action: val as AuditAction }, fromDay, toDay);
    }
  };

  const handleResourceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === 'all') {
      const rest = { ...filters };
      delete rest.resourceType;
      onFilterChange(rest, fromDay, toDay);
    } else {
      onFilterChange({ ...filters, resourceType: val as AuditResourceType }, fromDay, toDay);
    }
  };

  const handleResourceIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const trimmed = raw.trim();
    if (!trimmed) {
      const rest = { ...filters };
      delete rest.resourceId;
      onFilterChange(rest, fromDay, toDay);
    } else {
      onFilterChange({ ...filters, resourceId: trimmed }, fromDay, toDay);
    }
  };

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFrom = e.target.value;
    const range = toRange(newFrom, toDay);
    const rest = { ...filters };
    delete rest.from;
    delete rest.to;
    onFilterChange({ ...rest, ...range }, newFrom, toDay);
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTo = e.target.value;
    const range = toRange(fromDay, newTo);
    const rest = { ...filters };
    delete rest.from;
    delete rest.to;
    onFilterChange({ ...rest, ...range }, fromDay, newTo);
  };

  const hasAnyFilter =
    Boolean(filters.actorType) ||
    Boolean(filters.actorId) ||
    Boolean(filters.action) ||
    Boolean(filters.resourceType) ||
    Boolean(filters.resourceId) ||
    Boolean(filters.from) ||
    Boolean(filters.to) ||
    Boolean(fromDay) ||
    Boolean(toDay);

  const inputClass =
    'border border-neutral-300 rounded-md px-2 py-1.5 text-caption font-medium text-neutral-900 bg-white hover:bg-neutral-50 focus:outline-none focus:border-primary-600 focus:ring-2 focus:ring-primary-600/30';

  return (
    <div
      role="search"
      aria-label="Filter audit log entries"
      className="flex items-center gap-3 flex-wrap text-caption"
    >
      <label htmlFor={actorIdAttr} className="flex items-center gap-2 text-neutral-600">
        Actor
        <select
          id={actorIdAttr}
          data-testid="actor-select"
          value={selectedActorKey}
          onChange={handleActorChange}
          className={`${inputClass} pl-2 pr-7`}
        >
          <option value="all">All actors</option>
          {options?.actors.map((actor: AuditLogActorOption) => {
            const key = actorKey(actor);
            return (
              <option key={key} value={key}>
                {actor.label}
              </option>
            );
          })}
        </select>
      </label>

      <label htmlFor={actionIdAttr} className="flex items-center gap-2 text-neutral-600">
        Action
        <select
          id={actionIdAttr}
          data-testid="action-select"
          value={filters.action ?? 'all'}
          onChange={handleActionChange}
          className={`${inputClass} pl-2 pr-7`}
        >
          <option value="all">All actions</option>
          {options?.actions.map((act) => (
            <option key={act} value={act}>
              {getActionLabel(act)}
            </option>
          ))}
        </select>
      </label>

      <label htmlFor={resourceTypeIdAttr} className="flex items-center gap-2 text-neutral-600">
        Resource type
        <select
          id={resourceTypeIdAttr}
          data-testid="resource-type-select"
          value={filters.resourceType ?? 'all'}
          onChange={handleResourceTypeChange}
          className={`${inputClass} pl-2 pr-7`}
        >
          <option value="all">All resource types</option>
          {options?.resourceTypes.map((res) => (
            <option key={res} value={res}>
              {res.charAt(0).toUpperCase() + res.slice(1)}
            </option>
          ))}
        </select>
      </label>

      <label htmlFor={resourceIdAttr} className="flex items-center gap-2 text-neutral-600">
        Resource ID
        <input
          id={resourceIdAttr}
          data-testid="resource-id-input"
          type="text"
          placeholder="Filter by ID..."
          defaultValue={filters.resourceId ?? ''}
          onBlur={handleResourceIdChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleResourceIdChange(e as any);
            }
          }}
          className={`${inputClass} w-36`}
        />
      </label>

      <label htmlFor={fromDateAttr} className="flex items-center gap-2 text-neutral-600">
        From
        <input
          id={fromDateAttr}
          data-testid="from-date-input"
          type="date"
          value={fromDay}
          max={toDay || undefined}
          onChange={handleFromDateChange}
          className={inputClass}
        />
      </label>

      <label htmlFor={toDateAttr} className="flex items-center gap-2 text-neutral-600">
        To
        <input
          id={toDateAttr}
          data-testid="to-date-input"
          type="date"
          value={toDay}
          min={fromDay || undefined}
          onChange={handleToDateChange}
          className={inputClass}
        />
      </label>

      {hasAnyFilter && (
        <button
          id="filterClearBtn"
          data-testid="filter-clear-btn"
          type="button"
          onClick={onClear}
          className="text-caption font-medium text-neutral-600 hover:text-neutral-900 transition-colors cursor-pointer"
        >
          Clear filters
        </button>
      )}
    </div>
  );
};
